/**
 * NOTE: `addCartItemHelper` and `updateCartItemHelper` share overlapping logic
 * (see @remarks on each) and are candidates for consolidation later.
 *
 * They are used in different flows:
 *   - addCartItemHelper    → store/product listing (add-to-cart)
 *   - updateCartItemHelper → cart page quantity control
 *
 * DO NOT modify either function's behavior without confirming with a senior dev
 * first — see the @remarks blocks below for known edge-case differences.
 */

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

/**
 * Adds a product to the cart, or increments its quantity if it already exists.
 *
 * Used when adding an item from the store/product listing into the cart.
 * If the product (matched by product ID + variant ID) is already present,
 * its `orderQuantity` is increased by the incoming amount rather than replaced.
 *
 * @param {Object} params
 * @param {Array<CartProduct>} params.existingProducts - Current cart items. May be empty/undefined.
 * @param {CartProduct} params.product - Product to add, including the quantity to add.
 * @returns {Array<CartProduct>} New cart array (does not mutate `existingProducts`).
 *
 * @remarks
 * - Match key: `_id` + `variant._id`. Products are considered "the same" only
 *   if both match (e.g. same product, different variant = different cart line).
 * - Quantity is capped at 99 (`Math.min(newQty, 99)`), silently — no error/warning
 *   is surfaced if the cap is hit.
 * - New items are prepended (`[product, ...existingProducts]`), so most recently
 *   added items appear first.
 */
export function addCartItemHelper({ existingProducts, product }) {
  if (!existingProducts || existingProducts.length === 0) {
    return [product];
  }

  const existingProductIndex = existingProducts.findIndex(
    (item) =>
      item._id === product._id && item.variant._id === product.variant._id,
  );

  // Product not in cart yet
  if (existingProductIndex === -1) {
    return [product, ...existingProducts];
  }

  // Product already exists, update quantity
  return existingProducts.map((item, index) =>
    index === existingProductIndex
      ? {
          ...item,
          orderQuantity: Math.min(
            item.orderQuantity + product.orderQuantity,
            99,
          ),
        }
      : item,
  );
}

/**
 * Updates the quantity of a product already in the cart (e.g. from a quantity
 * stepper/input on the cart page). Sets `orderQuantity` to the given value —
 * does NOT add it to the existing quantity.
 *
 * @param {Object} params
 * @param {Array<CartProduct>} params.existingProducts - Current cart items. May be empty/undefined.
 * @param {CartProduct} params.product - Product with the new target `orderQuantity`.
 * @returns {Array<CartProduct>} New cart array (does not mutate `existingProducts`).
 *
 * @remarks
 * - Match key: same as `addCartItemHelper` (`_id` + `variant._id`).
 * - Quantity is capped at 99, same as `addCartItemHelper`.
 * - ⚠️ CAVEAT: if `existingProducts` is non-empty but does NOT contain a
 *   matching item, this function is a no-op — the product is silently
 *   dropped, not appended. This function assumes it is only ever called
 *   with a product that is already in the cart (e.g. from a cart-page
 *   input, where the item is guaranteed to be rendered). Do not reuse
 *   this for "add or update" flows — use `addCartItemHelper` for that.
 */

export function updateCartItemHelper({ existingProducts, product }) {
  if (!existingProducts || existingProducts.length === 0) {
    return [product];
  }

  // Product already exists, update quantity
  return existingProducts.map((item) =>
    item._id === product._id && item.variant._id === product.variant._id
      ? {
          ...item,
          orderQuantity: Math.min(product.orderQuantity, 99),
        }
      : item,
  );
}

export function calculateTotal(cart) {
  return cart.reduce((sum, item) => sum + item.price * item.qty, 0);
}

export const formatPrice = (value) => {
  const currency = "SAR";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(value);
};

//   const formatPrice  = (price) =>
// new Intl.NumberFormat("ar-SA", {
//   style: "currency",
//   currency,
// }).format(price);
